import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import type { Option } from '@openx/types/options';
import type React from 'react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AutocompleteConfigurableFilter } from '../../types';

export type AutocompleteSelectorRef = {
  closeDropdown: () => void;
  isDropdownOpen: boolean;
};

type AutocompleteSelectorProps = {
  options?: Option[];
  selectedOptions?: string[];
  onOptionChange: (options: string[]) => void;
  config?: AutocompleteConfigurableFilter<string>['config'];
  inputDataTest?: string;
  isDisabled?: boolean;
};

const getAutocompleteDropdownStyle = (dropdownHeight?: string) => {
  if (!dropdownHeight) return {};

  return {
    sx: {
      '> ul': {
        maxHeight: dropdownHeight,
        overflowY: 'auto',
      },
    },
  };
};

export const AutocompleteSelector = forwardRef<AutocompleteSelectorRef, AutocompleteSelectorProps>(
  ({ options, selectedOptions = [], onOptionChange, config, inputDataTest, isDisabled }, ref) => {
    const [selectedIds, setSelectedIds] = useState<string[]>(selectedOptions);
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();

    const { emptyPlaceholder, selectedPlaceholder } = {
      emptyPlaceholder: `[ ${t('Select option')} ]`,
      selectedPlaceholder: `[ ${t('Select or Search')} ]`,
      ...(config || {}),
    };

    useImperativeHandle(ref, () => ({
      isDropdownOpen: open,
      closeDropdown: () => setOpen(false),
    }));

    const handleChange = useCallback(
      (_event: React.SyntheticEvent, newValue: Option[]) => {
        const newIds = newValue.map(option => option.id);
        setSelectedIds(newIds);
        onOptionChange(newIds);
      },
      [onOptionChange]
    );

    const handleDelete = useCallback(
      (idToDelete: string) => () => {
        const newIds = selectedIds.filter(id => id !== idToDelete);
        setSelectedIds(newIds);
        onOptionChange(newIds);
      },
      [selectedIds, onOptionChange]
    );

    const handleOptionIconClick = useCallback(
      (event: React.SyntheticEvent, selected: boolean, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        let newIds = selectedIds;
        if (selected) {
          newIds = [...selectedIds.filter(itemId => itemId !== id)];
        } else {
          newIds = [...selectedIds, id];
        }

        setSelectedIds(newIds);
        onOptionChange(newIds);
      },
      [selectedIds, onOptionChange]
    );

    const selectedOptionsFull = selectedIds
      .map(id => options?.find(option => option.id === id))
      .filter((option): option is Option => option !== undefined);

    return (
      <Autocomplete
        data-test="autocomplete-filter-selector"
        multiple
        id="autocomplete-selector"
        disabled={isDisabled}
        loading={config?.isLoading}
        options={options ?? []}
        getOptionLabel={option => option.name}
        value={selectedOptionsFull}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={handleChange}
        renderInput={params => (
          <TextField
            data-test={inputDataTest ?? 'autocomplete-filter-input'}
            {...params}
            variant="outlined"
            placeholder={selectedOptionsFull.length === 0 ? emptyPlaceholder : selectedPlaceholder}
            sx={{
              '& .MuiAutocomplete-endAdornment': {
                right: '0 !important',
              },
              '& .MuiOutlinedInput-root': {
                paddingTop: '2px !important',
                paddingBottom: '0px !important',
                paddingLeft: '4px !important',
                right: '0 !important',
              },
            }}
            slotProps={{
              ...params.slotProps,

              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <>
                    {selectedOptionsFull.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={selectedOptionsFull[0].name}
                          onDelete={handleDelete(selectedOptionsFull[0].id)}
                          sx={{ height: 24 }}
                          deleteIcon={
                            <IconButton size="large" aria-label="remove-option">
                              <CloseIcon data-test="chip-delete" />
                            </IconButton>
                          }
                        />
                        {selectedOptionsFull.length > 1 && (
                          <Box sx={{ ml: 0.5 }}>
                            <Typography>+{selectedOptionsFull.length - 1}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {params.slotProps.input.startAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props;

          return (
            <ListItemButton
              key={key}
              component="li"
              {...rest}
              onClick={e => handleOptionIconClick(e, selected, option.id)}
              sx={theme => ({
                minHeight: 48,
                paddingTop: '6px',
                paddingBottom: '6px',
                [theme.breakpoints.up('sm')]: {
                  minHeight: 'auto',
                },
                '&:hover': { color: 'inherit' },
                '.MuiListItemIcon-root': { minWidth: 'auto' },
              })}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography
                  data-test={`autocomplete-dropdown-option-${option.name.toLowerCase()}`}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {option.name}
                </Typography>
                <ListItemIcon sx={{ ml: 'auto' }}>{selected ? <RemoveIcon /> : <AddIcon />}</ListItemIcon>
              </Stack>
            </ListItemButton>
          );
        }}
        renderValue={() => null}
        slotProps={{
          paper: getAutocompleteDropdownStyle(config?.dropdownHeight),
        }}
      />
    );
  }
);
